import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { oauthService } from '../services/oauth';
import { useRbacConfig } from '../contexts/RbacConfigContext';

// Lazy load pages
const Landing = lazy(() => import('../pages/Landing/Landing'));
const Login = lazy(() => import('../pages/LoginPage'));
const Dashboard = lazy(() => import('../pages/Dashboard/Dashboard'));
const Library = lazy(() => import('../pages/Library/Library'));
const PolicyViewer = lazy(() => import('../pages/PolicyViewer/PolicyViewer'));
const PolicyReaderPage = lazy(() => import('../pages/PolicyReader/PolicyReaderPage'));
const MyAcknowledgements = lazy(() => import('../pages/MyAcknowledgements/MyAcknowledgements'));
const AdminDashboard = lazy(() => import('../pages/AdminDashboard/AdminDashboard'));
const DocumentManager = lazy(() => import('../pages/DocumentManager/DocumentManager'));
const ApprovalWorkflow = lazy(() => import('../pages/ApprovalWorkflow/ApprovalWorkflow'));
const AcknowledgementTracking = lazy(() => import('../pages/AcknowledgementTracking/AcknowledgementTracking'));
const Rbac = lazy(() => import('../pages/Rbac/Rbac'));
const Search = lazy(() => import('../pages/Search/Search'));
const AIChat = lazy(() => import('../pages/AIChat/AIChat'));
const Profile = lazy(() => import('../pages/Profile/Profile'));
const TwoFactorAuthentication = lazy(() =>
  import('../pages/TwoFactorAuthentication/TwoFactorAuthentication')
);
const NotificationPreferences = lazy(() =>
  import('../pages/NotificationPreferences/NotificationPreferences')
);
const NotFound = lazy(() => import('../pages/NotFound'));
const AuthSuccess = lazy(() => import('../pages/AuthSuccess'));
const MfaLoginChallenge = lazy(() => import('../pages/MfaLoginChallenge'));
const MainLayout = lazy(() => import('../layouts/MainLayout'));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = oauthService.isAuthenticated();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const isAuthenticated = oauthService.isAuthenticated();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Admin route: only allow if user role can access this path
const AdminRoute = ({ children, path }) => {
  const isAuthenticated = oauthService.isAuthenticated();
  const { canAccessAdminPath, getAllowedAdminPaths } = useRbacConfig();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  const userInfo = oauthService.getUserInfo();
  const role = userInfo?.role || 'Employee';
  if (!canAccessAdminPath(role, path)) {
    const allowed = getAllowedAdminPaths(role);
    return <Navigate to={allowed[0] || '/dashboard'} replace />;
  }
  return children;
};

const AppRoutes = () => {
  return (
    <Router>
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
        <Routes>
          {/* Default route - redirect based on auth status */}
          <Route
            index
            element={
              oauthService.isAuthenticated() ?
                <Navigate to="/dashboard" replace /> :
                <Navigate to="/login" replace />
            }
          />

          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          <Route
            path="/landing"
            element={
              <PublicRoute>
                <Landing />
              </PublicRoute>
            }
          />

          {/* Auth callback route */}
          <Route path="/auth/success" element={<AuthSuccess />} />

          {/* MFA step after OAuth (pending JWT in query string; not stored until verified) */}
          <Route path="/auth/mfa" element={<MfaLoginChallenge />} />

          {/* Protected routes with MainLayout */}
          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route
              path="dashboard"
              element={<Dashboard />}
            />
            <Route
              path="library"
              element={<Library />}
            />
            <Route
              path="library/:categorySlug/:policySlug"
              element={<PolicyReaderPage />}
            />
            <Route
              path="policy-viewer"
              element={<PolicyViewer />}
            />
            <Route
              path="my-acknowledgements"
              element={<MyAcknowledgements />}
            />
            <Route
              path="admin-dashboard"
              element={
                <AdminRoute path="/admin-dashboard">
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route
              path="document-manager"
              element={
                <AdminRoute path="/document-manager">
                  <DocumentManager />
                </AdminRoute>
              }
            />
            <Route
              path="document-types"
              element={<Navigate to="/approval-workflow" replace />}
            />
            <Route
              path="approval-workflow"
              element={
                <AdminRoute path="/approval-workflow">
                  <ApprovalWorkflow />
                </AdminRoute>
              }
            />
            <Route
              path="sync-management"
              element={<Navigate to="/admin-dashboard" replace />}
            />
            <Route
              path="acknowledgement-tracking"
              element={
                <AdminRoute path="/acknowledgement-tracking">
                  <AcknowledgementTracking />
                </AdminRoute>
              }
            />
            <Route
              path="rbac"
              element={
                <AdminRoute path="/rbac">
                  <Rbac />
                </AdminRoute>
              }
            />
            <Route
              path="ask"
              element={<AIChat />}
            />
            <Route
              path="assistant"
              element={<Navigate to="/ask" replace />}
            />
            <Route
              path="search"
              element={<Search />}
            />
            <Route
              path="profile"
              element={<Profile />}
            />
            <Route
              path="settings/two-factor-authentication"
              element={<TwoFactorAuthentication />}
            />
            <Route
              path="settings/notifications"
              element={<NotificationPreferences />}
            />

          </Route>

          {/* Catch all route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRoutes;